import { useCallback, useEffect, useState } from 'react';
import {
  financeopsApi,
  type FeeRecord,
  type FinanceOpsDashboard,
  type FinanceRecommendation,
  type FinancialEventRecord,
  type FinancialHealthRecord,
  type LeakageRecord,
  type PayoutForecastRecord,
  type ReconciliationRecord,
  type SettlementRecord,
} from '../../api/financeops';

type Tab = 'dashboard' | 'settlements' | 'events' | 'fees' | 'reconciliation' | 'leakages' | 'forecasts' | 'health' | 'recommendations';

function statusClass(s: string) {
  if (s === 'paid' || s === 'matched' || s === 'resolved' || s === 'completed' || s === 'approved') return 'text-emerald-700 bg-emerald-50';
  if (s === 'pending' || s === 'processed' || s === 'investigating' || s === 'draft') return 'text-amber-700 bg-amber-50';
  if (s === 'mismatch' || s === 'open' || s === 'failed') return 'text-red-700 bg-red-50';
  return 'text-slate-600 bg-slate-50';
}

function severityClass(s?: string) {
  if (s === 'critical' || s === 'high') return 'text-red-700 bg-red-50';
  if (s === 'medium') return 'text-amber-700 bg-amber-50';
  return 'text-slate-600 bg-slate-50';
}

function scoreClass(score: number) {
  if (score >= 80) return 'text-emerald-700';
  if (score >= 60) return 'text-amber-700';
  return 'text-red-700';
}

function fmtCurrency(n: number) {
  if (Math.abs(n) >= 1000000) return `$${(n / 1000000).toFixed(1)}M`;
  if (Math.abs(n) >= 1000) return `$${(n / 1000).toFixed(1)}K`;
  return `$${n.toFixed(2)}`;
}

function eventTypeLabel(t: string) {
  return t.replace(/_/g, ' ');
}

export function FinanceOpsPage() {
  const [tab, setTab] = useState<Tab>('dashboard');
  const [dashboard, setDashboard] = useState<FinanceOpsDashboard | null>(null);
  const [settlements, setSettlements] = useState<SettlementRecord[]>([]);
  const [events, setEvents] = useState<FinancialEventRecord[]>([]);
  const [fees, setFees] = useState<FeeRecord[]>([]);
  const [reconciliation, setReconciliation] = useState<ReconciliationRecord[]>([]);
  const [leakages, setLeakages] = useState<LeakageRecord[]>([]);
  const [forecasts, setForecasts] = useState<PayoutForecastRecord[]>([]);
  const [health, setHealth] = useState<FinancialHealthRecord | null>(null);
  const [recommendations, setRecommendations] = useState<FinanceRecommendation[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, set, evt, fee, rec, leak, fcst, hlth, recs] = await Promise.all([
        financeopsApi.dashboard(), financeopsApi.listSettlements(), financeopsApi.listEvents(),
        financeopsApi.listFees(), financeopsApi.listReconciliation(), financeopsApi.listLeakages(),
        financeopsApi.listForecasts(), financeopsApi.getHealth(), financeopsApi.listRecommendations(),
      ]);
      setDashboard(dash); setSettlements(set); setEvents(evt); setFees(fee);
      setReconciliation(rec); setLeakages(leak); setForecasts(fcst); setHealth(hlth); setRecommendations(recs);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load finance operations data');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const generateRecs = async () => {
    setBusy(true);
    try { await financeopsApi.generateRecommendations(); await refresh(); } finally { setBusy(false); }
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: 'dashboard', label: 'Settlement Dashboard' },
    { id: 'settlements', label: 'Settlements' },
    { id: 'events', label: 'Financial Explorer' },
    { id: 'fees', label: 'Fee Analyzer' },
    { id: 'reconciliation', label: 'Reconciliation' },
    { id: 'leakages', label: 'Leakage Center' },
    { id: 'forecasts', label: 'Cash Flow' },
    { id: 'health', label: 'Financial Health' },
    { id: 'recommendations', label: 'AI Recommendations' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Settlement &amp; Finance Intelligence</h1>
          <p className="text-sm text-slate-500">Settlements, reconciliation, fee analysis &amp; AI financial copilot — APP-00009</p>
        </div>
        <button type="button" onClick={refresh} disabled={busy}
          className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700 disabled:opacity-50">Refresh</button>
      </div>

      {error && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-2">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium ${tab === t.id ? 'bg-sky-100 text-sky-700' : 'text-slate-600 hover:bg-slate-100'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'dashboard' && dashboard && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { label: 'Pending Payout', value: fmtCurrency(dashboard.pending_payout) },
            { label: 'Reserve Balance', value: fmtCurrency(dashboard.reserve_balance) },
            { label: 'Total Fees', value: fmtCurrency(dashboard.total_fees) },
            { label: 'Fee % of Revenue', value: `${dashboard.fee_pct_of_revenue.toFixed(1)}%` },
            { label: 'Reconciliation Rate', value: `${dashboard.reconciliation_rate.toFixed(0)}%` },
            { label: 'Open Leakages', value: dashboard.open_leakages.toString() },
            { label: 'Leakage Amount', value: fmtCurrency(dashboard.leakage_amount) },
            { label: 'Financial Health', value: dashboard.financial_health_score.toFixed(0) },
          ].map((kpi) => (
            <div key={kpi.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{kpi.label}</p>
              <p className="mt-1 text-2xl font-semibold text-slate-900">{kpi.value}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'settlements' && (
        <div className="space-y-3">
          {settlements.map((s) => (
            <div key={s.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-sm">{s.settlement_id}</span>
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusClass(s.status)}`}>{s.status}</span>
                <span className="text-lg font-semibold">{fmtCurrency(s.total_amount)}</span>
              </div>
              <div className="mt-2 flex flex-wrap gap-4 text-sm text-slate-600">
                <span>Reserve: {fmtCurrency(s.reserve_balance)}</span>
                {s.payment_ref && <span>Ref: {s.payment_ref}</span>}
                {s.bank_status && <span>Bank: {s.bank_status}</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'events' && (
        <div className="space-y-3">
          {events.map((e) => (
            <div key={e.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-violet-50 px-2 py-0.5 text-xs text-violet-700">{eventTypeLabel(e.event_type)}</span>
                <span className={`font-medium ${e.amount >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{fmtCurrency(e.amount)}</span>
              </div>
              {e.description && <p className="mt-1 text-sm text-slate-600">{e.description}</p>}
              <div className="mt-1 flex gap-4 text-xs text-slate-400">
                {e.sku && <span>{e.sku}</span>}
                {e.order_id && <span>{e.order_id}</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'fees' && (
        <div className="space-y-3">
          {fees.map((f) => (
            <div key={f.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{f.fee_type.replace(/_/g, ' ')}</span>
                <span className="text-lg font-semibold text-red-700">{fmtCurrency(f.amount)}</span>
                <span className="text-sm text-slate-500">{f.pct_of_revenue.toFixed(1)}% of revenue</span>
              </div>
              {f.trend_pct != null && f.trend_pct !== 0 && (
                <p className={`mt-1 text-xs ${f.trend_pct > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                  {f.trend_pct > 0 ? '+' : ''}{f.trend_pct.toFixed(1)}% vs prior period
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === 'reconciliation' && (
        <div className="space-y-3">
          {reconciliation.map((r) => (
            <div key={r.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm">{r.entity_type} · {r.entity_id}</span>
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusClass(r.status)}`}>{r.status}</span>
              </div>
              <div className="mt-2 flex gap-4 text-sm text-slate-600">
                <span>Expected: {fmtCurrency(r.expected_amount)}</span>
                <span>Actual: {fmtCurrency(r.actual_amount)}</span>
                {r.variance !== 0 && <span className="text-red-600">Variance: {fmtCurrency(r.variance)}</span>}
              </div>
              {r.description && <p className="mt-1 text-xs text-slate-400">{r.description}</p>}
            </div>
          ))}
        </div>
      )}

      {tab === 'leakages' && (
        <div className="space-y-3">
          {leakages.map((l) => (
            <div key={l.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${severityClass(l.severity)}`}>{l.severity}</span>
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusClass(l.status)}`}>{l.status}</span>
                <span className="font-medium text-red-700">{fmtCurrency(l.amount)}</span>
              </div>
              <h3 className="mt-1 font-medium text-slate-900">{l.title}</h3>
              {l.description && <p className="mt-1 text-sm text-slate-600">{l.description}</p>}
            </div>
          ))}
        </div>
      )}

      {tab === 'forecasts' && (
        <div className="space-y-3">
          {forecasts.map((f) => (
            <div key={f.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusClass(f.status)}`}>{f.status}</span>
                <span className="text-lg font-semibold text-emerald-700">{fmtCurrency(f.expected_payout)}</span>
              </div>
              <div className="mt-2 grid gap-2 sm:grid-cols-3 text-sm text-slate-600">
                <span>Reserve Release: {fmtCurrency(f.reserve_release)}</span>
                <span>Pending: {fmtCurrency(f.pending_amount)}</span>
                <span>Working Capital: {fmtCurrency(f.working_capital)}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'health' && health && (
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { label: 'Profit', score: health.profit_score },
              { label: 'Fee Efficiency', score: health.fee_efficiency_score },
              { label: 'Settlement Accuracy', score: health.settlement_accuracy },
              { label: 'Cash Flow', score: health.cash_flow_score },
              { label: 'Marketplace', score: health.marketplace_health },
              { label: 'Overall', score: health.financial_health_score },
            ].map((s) => (
              <div key={s.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{s.label}</p>
                <p className={`mt-1 text-2xl font-semibold ${scoreClass(s.score)}`}>{s.score.toFixed(0)}</p>
              </div>
            ))}
          </div>
          {health.issue && <p className="text-sm text-slate-600">{health.issue}</p>}
        </div>
      )}

      {tab === 'recommendations' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button type="button" onClick={generateRecs} disabled={busy}
              className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700 disabled:opacity-50">
              Generate AI Recommendations
            </button>
          </div>
          {recommendations.map((r) => (
            <div key={r.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <h3 className="font-medium text-slate-900">{r.title}</h3>
                  {r.description && <p className="mt-1 text-sm text-slate-600">{r.description}</p>}
                </div>
                <span className="rounded-full bg-sky-50 px-2 py-0.5 text-xs font-medium text-sky-700">#{r.priority_rank}</span>
              </div>
              <div className="mt-2 flex flex-wrap gap-4 text-sm text-slate-500">
                <span>{r.type.replace(/_/g, ' ')}</span>
                <span>Confidence: {(r.confidence_score * 100).toFixed(0)}%</span>
                {r.expected_impact && <span>Impact: {r.expected_impact}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
